"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Separator } from "@/components/ui/separator";
export default function Commitment() {
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: essayRef,
    isVisible: essayVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="5056" id="our-commitment" className="bg-background text-foreground relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-20 md:pt-28 pb-24 md:pb-32">
        <div ref={headlineRef} className={`grid grid-cols-12 gap-6 md:gap-8 mb-20 md:mb-28 transition-all duration-1000 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="col-span-12 md:col-span-9 order-2 md:order-1">
            <h2 className="font-default font-semibold text-foreground leading-[0.95] tracking-tighter text-[3.25rem] sm:text-7xl md:text-8xl lg:text-[8.5rem]">
              Clarity{" "}
              <span className="text-muted-foreground">&amp;</span>{" "}
              <em className="italic font-light text-primary">
                Insight
              </em>
            </h2>
          </div>

          <div className="col-span-12 md:col-span-3 order-1 md:order-2 flex md:flex-col md:items-start md:justify-start pt-2 md:pt-6">
            <div className="flex flex-col">
              <span className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-muted-foreground">
                Philosophy
              </span>
              <div className="hidden md:block w-px h-24 mt-6 bg-primary" />
            </div>
          </div>
        </div>

        <div ref={essayRef} className="grid grid-cols-12 gap-6 md:gap-10 relative">
          <div className={`col-span-12 md:col-span-7 transition-all duration-1000 ease-out ${essayVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="space-y-8 font-default text-lg md:text-xl leading-relaxed text-muted-foreground">
              <p>Resseti was built on a singular, technical observation: the majority of academic marks are lost not to a lack of knowledge, but to a failure of process. When a student misapplies a method or skips a critical step, they are not failing to understand the subject; they are failing to execute the logic required by the assessment.</p>

              <p>Setura, our diagnostic engine, was designed to map student responses directly against mark-scheme logic. By identifying the exact point of failure, whether it is a misread instruction or a procedural error, we provide educators with the data they need to intervene exactly where it&apos;s needed.</p>

              <figure className="my-12 pl-6 md:pl-10 relative">
                <div className="absolute left-0 top-2 bottom-2 w-px bg-primary" />
                <blockquote className="font-default italic font-light leading-snug text-2xl md:text-[2rem] text-foreground">
                  &ldquo;Assessment should not be a post-mortem of what was lost, but a diagnostic map of how to improve the process.&rdquo;
                </blockquote>
                <div className="mt-6 w-16 h-px bg-primary" />
                <figcaption className="mt-4 font-mono text-[0.7rem] tracking-[0.2em] uppercase text-muted-foreground">
                  Resseti Engineering Principles, 2024
                </figcaption>
              </figure>

              <p>
                We believe that education technology should function as infrastructure, not as a distraction. Our tools are built to integrate seamlessly into existing workflows, providing clear, actionable insights that respect the expertise of the teacher.
              </p>

              <p>
                We are committed to building a future where every student understands exactly why they lost a mark, and more importantly, exactly how to secure it next time. This is the core of the Resseti mission: clarity through diagnostic rigour.
              </p>
            </div>
          </div>

          <aside className={`col-span-12 md:col-span-4 md:col-start-9 mt-4 md:mt-16 md:sticky md:top-24 self-start transition-all duration-1000 delay-200 ease-out ${essayVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <figure className="relative">
              <div className="relative overflow-hidden border border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/lumina_logic_trace.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/lumina_logic_trace.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/lumina_logic_trace.webp-wvc-sizes" alt="Visual representation of the Setura logic trace" loading="lazy" decoding="async" className="w-full h-auto object-cover" />
                <div className="absolute inset-0 bg-primary mix-blend-color opacity-15 pointer-events-none" />
              </div>
              <div className="mt-4 flex items-start gap-3">
                <div className="mt-2 w-6 h-px flex-shrink-0 bg-primary" />
                <figcaption className="font-mono text-[0.65rem] tracking-[0.22em] uppercase leading-relaxed text-muted-foreground">
                  Setura Diagnostic
                  <br />
                  Logic Trace, 2024
                </figcaption>
              </div>
            </figure>
          </aside>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        <Separator className="bg-border" />
      </div>
    </section>;
}